import React, { useEffect, useState, useCallback } from "react";
import { authFetch, C } from "./App";
import { ALERT_TYPES, GHANA_REGIONS, SEVERITY_COLORS } from "./alertTypes";

const EXPIRES_OPTIONS = [
  { hours: 1, label: "1 hour" },
  { hours: 6, label: "6 hours" },
  { hours: 24, label: "24 hours" },
  { hours: 72, label: "72 hours" },
];

function typeMeta(id) {
  return ALERT_TYPES.find((t) => t.id === id) || { label: id, icon: "📢", severity: "advisory" };
}

export function AlertsView({ token }) {
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [actingId, setActingId] = useState(null);
  const [showSend, setShowSend] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    authFetch("/admin/alerts", token)
      .then((d) => setAlerts(d.alerts || []))
      .catch((e) => setErr(e.message))
      .finally(() => setLoading(false));
  }, [token]);

  useEffect(() => { load(); }, [load]);

  function expireNow(id) {
    setActingId(id);
    authFetch(`/admin/alerts/${id}/expire`, token, { method: "PATCH" })
      .then(load)
      .catch((e) => setErr(e.message))
      .finally(() => setActingId(null));
  }

  function remove(id) {
    if (!window.confirm("Permanently delete this alert? This cannot be undone.")) return;
    setActingId(id);
    authFetch(`/admin/alerts/${id}`, token, { method: "DELETE" })
      .then(load)
      .catch((e) => setErr(e.message))
      .finally(() => setActingId(null));
  }

  return (
    <div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 4 }}>
        <h1 style={{ fontSize: 22, fontWeight: 700, color: "#111", margin: 0 }}>Alerts</h1>
        <button onClick={() => setShowSend(true)} style={{ background: C.green, color: "#fff", border: "none", borderRadius: 8, padding: "9px 16px", fontSize: 13, fontWeight: 600, cursor: "pointer" }}>+ Send Alert</button>
      </div>
      <p style={{ fontSize: 13, color: C.gray, margin: "0 0 20px" }}>Broadcast a public safety alert to the citizen app · shown to everyone (or a specific region) until it expires</p>

      {err && <div style={{ color: C.danger, fontSize: 13, marginBottom: 14 }}>{err}</div>}

      {loading ? (
        <div style={{ color: C.gray, fontSize: 13 }}>Loading…</div>
      ) : alerts.length === 0 ? (
        <div style={{ color: C.gray, fontSize: 13, padding: 30, textAlign: "center", background: C.white, borderRadius: 12, border: `1px solid ${C.border}` }}>No alerts yet. Click "+ Send Alert" to broadcast one.</div>
      ) : (
        <div style={{ background: C.white, border: `1px solid ${C.border}`, borderRadius: 12, overflow: "hidden" }}>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
            <thead>
              <tr style={{ background: C.grayLight, textAlign: "left" }}>
                <Th>Type</Th><Th>Message</Th><Th>Region</Th><Th>Severity</Th><Th>Status</Th><Th>Sent</Th><Th></Th>
              </tr>
            </thead>
            <tbody>
              {alerts.map((a) => {
                const meta = typeMeta(a.type);
                const active = new Date(a.expires_at) > new Date();
                const sevColor = SEVERITY_COLORS[a.severity] || SEVERITY_COLORS.advisory;
                return (
                  <tr key={a.id} style={{ borderTop: `1px solid ${C.border}` }}>
                    <Td><span style={{ marginRight: 6 }}>{meta.icon}</span><b>{meta.label}</b></Td>
                    <Td style={{ color: "#333", maxWidth: 280, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={a.message}>{a.message}</Td>
                    <Td style={{ color: C.gray, textTransform: "capitalize" }}>{a.region === "all" ? "All Regions" : (a.region || "").replace(/-/g, " ")}</Td>
                    <Td><span style={{ fontSize: 10.5, fontWeight: 600, color: sevColor, background: sevColor + "1A", padding: "2px 8px", borderRadius: 8, textTransform: "uppercase" }}>{a.severity}</span></Td>
                    <Td>
                      <span style={{ fontSize: 10.5, fontWeight: 600, color: active ? C.success : "#777", background: active ? "#E8F5E9" : C.grayLight, padding: "2px 8px", borderRadius: 8 }}>
                        {active ? "ACTIVE" : "EXPIRED"}
                      </span>
                    </Td>
                    <Td style={{ color: C.gray }}>{new Date(a.created_at).toLocaleString()}</Td>
                    <Td>
                      <div style={{ display: "flex", gap: 6 }}>
                        {active && <SmallBtn onClick={() => expireNow(a.id)} disabled={actingId === a.id} color={C.warn}>Expire Now</SmallBtn>}
                        <SmallBtn onClick={() => remove(a.id)} disabled={actingId === a.id} color={C.danger}>Delete</SmallBtn>
                      </div>
                    </Td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {showSend && <SendAlertModal token={token} onClose={() => setShowSend(false)} onSent={() => { setShowSend(false); load(); }} />}
    </div>
  );
}

function Th({ children }) { return <th style={{ padding: "10px 14px", fontSize: 11, fontWeight: 700, color: C.gray, textTransform: "uppercase", letterSpacing: 0.5 }}>{children}</th>; }
function Td({ children, style, title }) { return <td style={{ padding: "10px 14px", ...style }} title={title}>{children}</td>; }
function SmallBtn({ onClick, disabled, color, children }) {
  return <button onClick={onClick} disabled={disabled} style={{ background: "transparent", border: `1px solid ${color}55`, color, borderRadius: 6, padding: "4px 8px", fontSize: 11, fontWeight: 600, cursor: disabled ? "default" : "pointer", opacity: disabled ? 0.5 : 1 }}>{children}</button>;
}

function SendAlertModal({ token, onClose, onSent }) {
  const [form, setForm] = useState({ type: "", message: "", region: "all", severity: "warning", expires_hours: 24 });
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);

  function pickType(id) {
    const meta = typeMeta(id);
    setForm((f) => ({
      ...f,
      type: id,
      severity: meta.severity,
      message: f.message.trim() === "" || f.message === typeMeta(f.type).desc ? (meta.desc || "") : f.message,
    }));
  }

  function submit(e) {
    e.preventDefault();
    if (!form.type || !form.message.trim()) return;
    setBusy(true); setErr("");
    authFetch("/admin/alerts", token, {
      method: "POST",
      body: JSON.stringify({
        type: form.type,
        message: form.message.trim(),
        region: form.region,
        severity: form.severity,
        expires_hours: Number(form.expires_hours),
      }),
    })
      .then(onSent)
      .catch((e) => setErr(e.message))
      .finally(() => setBusy(false));
  }

  return (
    <div onClick={onClose} style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.4)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 100, padding: 16 }}>
      <form onClick={(e) => e.stopPropagation()} onSubmit={submit} style={{ background: "#fff", borderRadius: 14, padding: 24, width: 440, maxHeight: "88vh", overflowY: "auto", boxShadow: "0 20px 60px rgba(0,0,0,0.3)" }}>
        <div style={{ fontSize: 16, fontWeight: 700, marginBottom: 16 }}>Send Alert</div>
        {err && <div style={{ color: C.danger, fontSize: 12.5, marginBottom: 12 }}>{err}</div>}

        <Field label="Type" required>
          <select value={form.type} onChange={(e) => pickType(e.target.value)} required style={inputStyle}>
            <option value="">— Select alert type —</option>
            {ALERT_TYPES.map((t) => <option key={t.id} value={t.id}>{t.icon} {t.label}</option>)}
          </select>
        </Field>

        <Field label="Message" required>
          <textarea
            value={form.message}
            onChange={(e) => setForm((f) => ({ ...f, message: e.target.value }))}
            rows={3} required style={{ ...inputStyle, resize: "vertical" }}
            placeholder="What should citizens do or know?"
          />
        </Field>

        <Row>
          <Field label="Region">
            <select value={form.region} onChange={(e) => setForm((f) => ({ ...f, region: e.target.value }))} style={inputStyle}>
              <option value="all">All Regions</option>
              {GHANA_REGIONS.map((r) => <option key={r.id} value={r.id}>{r.name}</option>)}
            </select>
          </Field>
          <Field label="Severity">
            <select value={form.severity} onChange={(e) => setForm((f) => ({ ...f, severity: e.target.value }))} style={inputStyle}>
              <option value="advisory">Advisory</option>
              <option value="warning">Warning</option>
              <option value="critical">Critical</option>
            </select>
          </Field>
        </Row>

        <Field label="Expires In">
          <select value={form.expires_hours} onChange={(e) => setForm((f) => ({ ...f, expires_hours: e.target.value }))} style={inputStyle}>
            {EXPIRES_OPTIONS.map((o) => <option key={o.hours} value={o.hours}>{o.label}</option>)}
          </select>
        </Field>

        <div style={{ display: "flex", gap: 8, marginTop: 8 }}>
          <button type="button" onClick={onClose} style={{ flex: 1, background: C.grayLight, border: "none", borderRadius: 8, padding: "10px 0", fontSize: 13, cursor: "pointer" }}>Cancel</button>
          <button type="submit" disabled={busy} style={{ flex: 1, background: C.green, color: "#fff", border: "none", borderRadius: 8, padding: "10px 0", fontSize: 13, fontWeight: 600, cursor: "pointer" }}>{busy ? "Sending…" : "Send Alert"}</button>
        </div>
      </form>
    </div>
  );
}

function Row({ children }) {
  return <div style={{ display: "flex", gap: 10 }}>{React.Children.map(children, (c) => <div style={{ flex: 1 }}>{c}</div>)}</div>;
}

function Field({ label, required, children }) {
  return (
    <div style={{ marginBottom: 12 }}>
      <label style={{ display: "block", fontSize: 11.5, fontWeight: 600, color: C.gray, marginBottom: 5, textTransform: "uppercase" }}>
        {label}{required && <span style={{ color: C.danger }}> *</span>}
      </label>
      {children}
    </div>
  );
}

const inputStyle = { width: "100%", boxSizing: "border-box", padding: "9px 12px", border: "1px solid #e4e7ed", borderRadius: 8, fontSize: 13 };
